---
title: Privacy Policy - SmartAcademy
navigation_label: Privacy Policy
description: Discover how SmartAcademy protects student, teacher, and parent data. This Privacy Policy outlines how academic records, communications, and school data are securely managed and shared.
---

At SmartAcademy by WhatsDiscuss ("we", "us", or "our"), we are committed to safeguarding your privacy. This Privacy Policy outlines how we handle the information you provide, including the specific details related to the roles of Admin, Principal, and Teachers. By using our services, you consent to the practices described in this policy.

---

## Data Collection

We collect and store information necessary for the efficient functioning of the SmartAcademy platform, which is designed specifically for schools and colleges in India. The types of data collected are based on the roles within the application:

### Admin
The Admin has access to all data within the system, including information related to principals, teachers, classes, sections, subjects, students, and student attendance. Admins can send attendance updates to parents and assign homework to students.
- **Collected Data**: Principal and teacher profiles, class and section details, subject information, student data, student attendance records, and homework assignments.

### Principal
The Principal can access teacher information, manage classes and sections, subjects, student data, and student attendance. Principals can send attendance updates to parents and assign homework to students.
- **Collected Data**: Teacher profiles, class and section details, subject information, student data, and student attendance records.

### Teachers
Teachers have access to student data and can manage student attendance. Teachers can send homework updates to parents.
- **Collected Data**: Student data, student attendance records.

### Data Storage
All data, including principals, teachers, classes, sections, subjects, students, and attendance records, is securely stored on the WhatsDiscuss server.

---

## Data Usage

Data collected within SmartAcademy is used exclusively for educational purposes, including communication, attendance tracking, and homework management:

1. **Attendance Updates**: Admins and Principals can send attendance updates to a parent’s WhatsApp number. These updates are stored as a message within WhatsDiscuss and are not retained elsewhere.
  
2. **Homework Assignments**: When teachers or principals send homework updates to a parent’s WhatsApp number, it is stored as a message within WhatsDiscuss and is not retained elsewhere.

We do not share your information with any external parties. Your data is strictly used within the SmartAcademy platform to enhance and support educational communication between users.

---

## Access to Data

Access to data is role-specific within the SmartAcademy platform:

1. **Admin Access**: Full access to all platform data, including principals, teachers, classes, sections, subjects, students, attendance records, and the ability to send attendance updates and homework.
  
2. **Principal Access**: Access to teachers, classes, sections, subjects, students, and attendance records, with the ability to send attendance updates and homework.
  
3. **Teacher Access**: Access to student data and attendance records, with the ability to send homework to parents.

---

## Security Measures

We take the security of your information seriously. To protect your data against unauthorized access, disclosure, or modification, we employ industry-standard security measures:

1. **Encryption**: Every piece of data transferred to our server is encrypted during transmission to ensure security. Sensitive data, including attendance records and homework assignments, is encrypted both in transit and at rest.
  
2. **Access Controls**: We use role-based access controls to ensure that only authorized individuals can access specific data. Access levels are based on user roles: Admin, Principal, and Teacher.
  
3. **Regular Audits**: We conduct regular security audits and reviews to maintain the integrity and confidentiality of our platform.

---

## Data Handling and Retention

Data within the SmartAcademy platform is retained only as long as necessary for educational and communication purposes:

1. **Attendance and Homework Messages**: Attendance updates and homework assignments are stored as messages within WhatsDiscuss. Attendance updates and homework messages are stored temporarily and are not retained beyond their immediate purpose.
  
2. **Educational Data**: Information such as student attendance and profiles is retained while relevant to the educational purposes of the platform. If data is no longer needed, it is securely deleted.

---

## Data Location and Indian Compliance

SmartAcademy is designed exclusively for schools and colleges in India. All data collected and processed is handled within India, and we ensure that all data management practices comply with applicable Indian data protection laws and regulations.

---

## User Rights and Data Access

You have rights related to the data you provide:

1. **View and Edit**: Users (based on their role) have the ability to view and update data related to their profile and educational responsibilities.
  
2. **Data Deletion**: Individual messages, such as attendance updates and homework, cannot be deleted separately. If you wish to remove a message, you must delete the relevant client record or data.

---

## Changes to this Policy

We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. Any updates will be posted on this page, and we encourage you to review this policy regularly. Your continued use of our services after any modifications constitutes your acceptance of the revised policy.

---

## Contact Us

If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:
- **Email**: [support@whatsdiscuss.in](mailto:support@whatsdiscuss.in)
- **WhatsApp**: [+91 93103 25556](https://wa.me/919310325556)

We are committed to addressing any queries or concerns you may have about the privacy of your data.
